import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { AssessmentRiskBanner } from "@/components/AssessmentRiskBanner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import NotificationBell from "@/components/NotificationBell";
import ThemeToggle from "@/components/ThemeToggle";
import UserModeToggle from "@/components/UserModeToggle";
import { ModeSwitchFooter } from "@/components/ModeSwitchFooter";
import { NavInfo } from "@/components/NavInfo";

const PERSONAL_INFO = "Your contact information, password and account settings.";
import { useUserMode } from "@/context/UserModeContext";
import {
  LayoutDashboard, CalendarDays, Users, LineChart, FileText, LogOut, Zap, Settings,
  Megaphone, CalendarX, Inbox, ShieldCheck, Sliders, User, Users as UsersIcon, Menu, X,
  Award, Repeat, KeyRound, Package, BookOpen, Search, Mail, Ban, Rss, Send, Trophy,
  PartyPopper, Coins,
} from "lucide-react";

export default function Layout({ children }) {
  const { user, logout, viewMode, setViewMode } = useAuth();
  const { isSimple } = useUserMode();
  const nav = useNavigate();
  const loc = useLocation();
  // Iter288/292 — In the Festival OPS view we auto-hide the sidebar to
  // a narrow icon strip so the court grid gets max screen space. It
  // slides out to the full-width version on hover of the strip. The
  // OPS route is `/festival/:id/live` (NOT `/ops` — earlier attempts
  // used the wrong path and silently no-op'd).
  const isFestivalOps = /^\/festival\/[^/]+\/live/.test(loc.pathname || "");
  const [sidebarHover, setSidebarHover] = useState(false);
  const sidebarCollapsed = isFestivalOps && !sidebarHover;
  const [mobileOpen, setMobileOpen] = useState(false);
  const perms = user?.permissions || [user?.role].filter(Boolean);
  const isSuperAdmin = perms.includes("super_admin");
  const isAdmin = isSuperAdmin || perms.includes("admin") || user?.role === "admin";
  const isCoach = isAdmin || perms.includes("coach") || user?.role === "coach";

  // Admins with manage_users see a live count of pending player approvals
  // as a chip next to the "Admin console" nav item. Super-admin gets the
  // perm implicitly; the /pending endpoint 403s otherwise so we gate the
  // fetch as well.
  const canManageUsers = isSuperAdmin || perms.includes("manage_users");
  const [pendingApprovals, setPendingApprovals] = useState(0);
  useEffect(() => {
    if (!canManageUsers) return;
    let cancelled = false;
    const fetchPending = () => {
      api.get("/admin/players/pending")
        .then((r) => { if (!cancelled) setPendingApprovals(Array.isArray(r.data) ? r.data.length : 0); })
        .catch(() => { if (!cancelled) setPendingApprovals(0); });
    };
    fetchPending();
    // Re-poll every 60s so newly approved players clear the badge without a hard refresh.
    const t = setInterval(fetchPending, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, [canManageUsers]);

  // Dual-role: a coach/admin can flip to student lens if admin has enabled it.
  const dualRole = !!user?.dual_role_enabled && isCoach;
  // Effective view: 'coach' for coaches in coach mode, 'student' if they flipped OR they're a plain student.
  const effectiveStudent = !isCoach || (dualRole && viewMode === "student");

  // Student nav (used when effectiveStudent is true)
  const studentItems = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/calendar", label: "Calendar", icon: CalendarDays },
    { to: "/find-coach", label: "Find a coach", icon: Search },
    { to: "/feed", label: "Feed", icon: Rss },
    { to: "/offerings", label: "Offerings", icon: Megaphone },
    { to: "/requests", label: "Requests", icon: Inbox },
    { to: "/progress", label: "My Progress", icon: LineChart },
    { to: "/credits", label: "My Credits", icon: Coins },
    { to: "/badges", label: "Trophies", icon: Trophy },
    { to: "/reports", label: "Reports", icon: FileText },
  ];

  // Pending / rejected players can't reach any of the app pages — mirror the
  // ProtectedRoute gate here so the sidebar doesn't dangle 8 dead links.
  const isPendingPlayer = user?.role === "student" && (
    user?.player_status === "pending"
    || user?.player_status === "pending_details"
    || user?.player_status === "rejected"
  );
  const pendingItems = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/profile/personal", label: "Personal details", icon: User, info: PERSONAL_INFO },
  ];

  // Coach + admin nav
  const coachItems = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/calendar", label: "Calendar", icon: CalendarDays },
    { to: "/offerings", label: "Offerings", icon: Megaphone },
    { to: "/availability", label: "Availability", icon: CalendarX },
    { to: "/requests", label: "Requests", icon: Inbox },
    { to: "/players", label: "Players", icon: Users },
    { to: "/badges", label: "Trophies", icon: Trophy },
    { to: "/coach/followers", label: "Followers", icon: Send },
    { to: "/packages", label: "Packages", icon: Package },
    { to: "/teams", label: "Teams", icon: UsersIcon },
    { to: "/drills", label: "Drill Library", icon: BookOpen },
    { to: "/admin/emails", label: "Bulk emails", icon: Mail },
    { to: "/coach/profile", label: "My Coaching Profile", icon: User, info: "Your public profile, coaching credentials and certifications." },
    ...(isAdmin && user?.id ? [{ to: `/admin/users/${user.id}`, label: "My Bookings & Income", icon: Coins, info: "Your session history, payments and credit usage." }] : []),
    { to: "/profile/personal", label: "Personal details", icon: User, info: PERSONAL_INFO },
    ...(isAdmin ? [{ to: "/admin", label: "Admin console", icon: ShieldCheck, accent: true, badge: pendingApprovals || 0 }] : []),
    ...(isAdmin ? [{ to: "/admin/applications", label: "Applications", icon: ShieldCheck }] : []),
    ...(isAdmin ? [{ to: "/admin/inbox", label: "Comms", icon: Inbox }] : []),
    ...(isSuperAdmin ? [{ to: "/admin/users", label: "Users & roles", icon: ShieldCheck }] : []),
    ...(isSuperAdmin ? [{ to: "/admin/audit", label: "Audit log", icon: ShieldCheck }] : []),
    ...(isAdmin ? [{ to: "/admin/features", label: "Features", icon: Sliders }] : []),
    ...(isAdmin ? [{ to: "/settings/pricing", label: "Pricing", icon: Settings }] : []),
  ];

  const items = isPendingPlayer ? pendingItems : (effectiveStudent ? studentItems : coachItems);

  // Iter303 — Festival Organiser lens. When the flag is on AND the user
  // is not a coach/admin (who keep their full nav), swap the nav for a
  // festival-only tile set so the org-side UI doesn't confuse them with
  // coaching tools. Coaches/admins with the flag still get their normal
  // nav plus the Festivals shortcut.
  const isOrganiser = !!user?.is_festival_organiser;
  const festivalItems = [
    { to: "/festival/mine", label: "My festivals", icon: PartyPopper },
    { to: "/festival/new", label: "New festival", icon: Zap },
    { to: "/festival", label: "Pricing & plans", icon: Sliders },
    { to: "/profile/personal", label: "Personal details", icon: User, info: PERSONAL_INFO },
    { to: "/account/password", label: "Password", icon: KeyRound },
  ];
  const navItems = (isOrganiser && !isCoach && !isPendingPlayer) ? festivalItems : items;

  const handleLogout = async () => {
    if (window.__rallyConfirmLeave?.(() => handleLogoutNow())) return;
    await handleLogoutNow();
  };
  const handleLogoutNow = async () => {
    setMobileOpen(false);
    await logout();
    nav("/login");
  };

  const closeMobile = () => setMobileOpen(false);

  const flipMode = () => {
    if (window.__rallyConfirmLeave?.(() => flipModeNow())) return;
    flipModeNow();
  };
  const flipModeNow = () => {
    setViewMode(viewMode === "student" ? "coach" : "student");
    setMobileOpen(false);
    nav("/dashboard");
  };

  const brand = (onClick) => (
    <Link to="/dashboard" onClick={onClick} className="flex items-center gap-2 min-w-0" data-testid="brand-link">
      <div className="w-8 h-8 bg-[#CCFF00] flex items-center justify-center shrink-0">
        <Zap strokeWidth={2.5} className="w-5 h-5 text-black" />
      </div>
      <div className="min-w-0">
        <div className="h-display text-xl leading-none tracking-tighter truncate">RALLY LAB</div>
        <div className="label-mono text-[0.6rem]">Pickleball OS</div>
      </div>
    </Link>
  );

  const navList = (onItemClick, iconsOnly = false) => (
    <nav className="flex flex-col gap-1 flex-1 min-h-0 overflow-y-auto overscroll-contain -mx-1 px-1" data-testid="sidebar-nav-scroll">
      {navItems.map(({ to, label, icon: Icon, badge, info }) => (
        <NavLink
          key={to}
          to={to}
          onClick={onItemClick}
          data-testid={`nav-${to.slice(1)}`}
          title={iconsOnly ? label : undefined}
          className={({ isActive }) =>
            `flex items-center gap-3 ${iconsOnly ? "justify-center px-2" : "px-3"} py-2.5 text-sm transition-colors ${
              isActive
                ? "bg-[#CCFF00] text-black font-semibold"
                : "text-[#A3A3A3] hover:bg-[#141414] hover:text-white"
            }`
          }
        >
          <Icon strokeWidth={1.5} className="w-4 h-4" />
          {!iconsOnly && <span className="uppercase tracking-wider text-xs flex-1">{label}</span>}
          {!iconsOnly && info && <NavInfo text={info} testid={`nav-info-${to.slice(1).replace(/\//g, "-")}`} />}
          {!iconsOnly && badge > 0 && (
            <span
              className="ml-auto inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 text-[10px] font-bold bg-[#FF4500] text-white tracking-tight"
              data-testid={`nav-badge-${to.slice(1)}`}
              title={`${badge} pending`}
            >
              {badge > 99 ? "99+" : badge}
            </span>
          )}
          {iconsOnly && badge > 0 && (
            <span
              className="absolute ml-6 -mt-4 inline-flex items-center justify-center min-w-[0.9rem] h-3 px-1 text-[8px] font-bold bg-[#FF4500] text-white"
              data-testid={`nav-badge-${to.slice(1)}`}
            >{badge > 9 ? "9+" : badge}</span>
          )}
        </NavLink>
      ))}
    </nav>
  );

  // Mode switcher — only visible for dual-role users
  const modeSwitcher = dualRole ? (
    <button
      type="button"
      onClick={flipMode}
      data-testid="mode-switcher"
      className="w-full mb-3 px-3 py-2.5 border border-[#262626] hover:border-[#CCFF00] flex items-center justify-between gap-2 transition-colors group"
    >
      <div className="flex items-center gap-2 min-w-0">
        {effectiveStudent
          ? <Award strokeWidth={1.75} className="w-4 h-4 text-[#FF4500] shrink-0" />
          : <Users strokeWidth={1.75} className="w-4 h-4 text-[#CCFF00] shrink-0" />}
        <div className="text-left min-w-0">
          <div className="label-mono text-[9px] leading-none mb-0.5">VIEWING AS</div>
          <div className="text-xs font-bold uppercase tracking-wider truncate" style={{ color: effectiveStudent ? "#FF4500" : "#CCFF00" }}>
            {effectiveStudent ? "STUDENT" : "COACH"}
          </div>
        </div>
      </div>
      <Repeat strokeWidth={1.75} className="w-3.5 h-3.5 text-[#A3A3A3] group-hover:text-white shrink-0" />
    </button>
  ) : null;

  const userFooter = (
    <div className="border-t border-[#262626] pt-4 shrink-0">
      {modeSwitcher}
      <Link to="/profile/personal" onClick={closeMobile} data-testid="sidebar-profile-link"
        className="block mb-3 group" title="View and edit your profile">
        <div className="text-sm font-medium group-hover:text-[#CCFF00] transition-colors underline-offset-4 group-hover:underline" data-testid="sidebar-user-name">{user?.name}</div>
        <div className="label-mono text-[0.65rem] mt-0.5">{user?.role}{dualRole ? " · DUAL-ROLE" : ""}</div>
      </Link>
      <Link
        to="/account/password"
        onClick={closeMobile}
        data-testid="nav-change-password"
        className="flex items-center gap-2 text-xs text-[#A3A3A3] hover:text-white transition-colors uppercase tracking-widest mb-3"
      >
        <KeyRound className="w-3.5 h-3.5" strokeWidth={1.5} /> Change password
      </Link>
      <button
        data-testid="logout-button"
        onClick={handleLogout}
        className="flex items-center gap-2 text-xs text-[#A3A3A3] hover:text-[#FF4500] transition-colors uppercase tracking-widest"
      >
        <LogOut className="w-3.5 h-3.5" strokeWidth={1.5} /> Sign out
      </button>
    </div>
  );

  const showProfileIcon = isSimple && user?.role === "student";
  const profileIcon = (testid) => (
    <Link to="/profile/personal" aria-label="My profile" title="My profile" data-testid={testid}
      className="p-2 text-white hover:text-[#CCFF00] transition-colors">
      <User strokeWidth={1.75} className="w-5 h-5" />
    </Link>
  );

  return (
    <div className="min-h-screen lg:flex bg-[#0A0A0A] text-white noise-bg" data-user-mode={isSimple ? "simple" : "advanced"}>
      {/* Desktop sidebar — hidden in Simple Mode (iter76). Auto-hides
          to an icon strip in Festival OPS view (iter288).
          Iter376 · Fix 1 — REVERTED Iter374 Fix 3. The sidebar is
          restored on `/festival/{id}/live` (it auto-collapses to a
          56px icon strip via sidebarCollapsed) so organisers keep
          access to Advanced settings and the rest of the app nav.
          The Layout <main> width-recovery on this route (below)
          still reclaims the 1400px reading cap. */}
      {!isSimple && (
        <aside
          data-testid="app-sidebar"
          data-collapsed={sidebarCollapsed ? "true" : "false"}
          onMouseEnter={() => isFestivalOps && setSidebarHover(true)}
          onMouseLeave={() => isFestivalOps && setSidebarHover(false)}
          className={`hidden lg:flex ${sidebarCollapsed ? "w-14 p-2" : "w-64 p-6"} border-r border-[#262626] bg-[#0A0A0A] flex-col sticky top-0 h-screen shrink-0 transition-all duration-200`}
        >
          <div className={sidebarCollapsed ? "mb-4 flex justify-center" : "mb-10"}>
            {sidebarCollapsed ? (
              <Link to="/dashboard" className="w-8 h-8 bg-[#CCFF00] flex items-center justify-center" data-testid="brand-link-mini" title="Rally Lab">
                <Zap strokeWidth={2.5} className="w-5 h-5 text-black" />
              </Link>
            ) : brand()}
          </div>
          {navList(undefined, sidebarCollapsed)}
          {!sidebarCollapsed && userFooter}
        </aside>
      )}

      {/* Mobile top bar — simplified when Simple Mode (no hamburger) */}
      <header className="lg:hidden sticky top-0 z-40 flex items-center justify-between px-4 py-3 border-b border-[#262626] bg-[#0A0A0A]/95 backdrop-blur">
        {brand()}
        <div className="flex items-center gap-2">
          <div data-testid="user-mode-toggle-mobile-wrap"><UserModeToggle compact homeOnly /></div>
          <div data-testid="theme-toggle-mobile-wrap"><ThemeToggle compact /></div>
          <div data-testid="mobile-bell-wrap"><NotificationBell /></div>
          {showProfileIcon && profileIcon("simple-profile-icon-mobile")}
          {!isSimple && (
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
              data-testid="mobile-menu-button"
              className="p-2 -mr-2 text-white hover:text-[#CCFF00] transition-colors"
            >
              <Menu strokeWidth={1.75} className="w-6 h-6" />
            </button>
          )}
        </div>
      </header>

      {/* Desktop floating bell + theme toggle + user-mode toggle.
          Iter377 · Fix 1 — Sits inside a translucent pill so it
          reads as a distinct top-right cluster, never blending
          into the Live Ops toolbar rows below. Pages that begin
          near the top now reserve headroom via their own
          `lg:pt-16` (see FestivalLiveOps.jsx) so the toolbar
          never underlaps this cluster. */}
      <div className="hidden lg:flex fixed top-3 right-6 z-40 items-center gap-2 px-3 py-1.5 bg-[#0A0A0A]/85 backdrop-blur border border-[#262626] rounded-full shadow-lg" data-testid="desktop-bell-wrap">
        <UserModeToggle homeOnly />
        <div data-testid="theme-toggle-desktop-wrap"><ThemeToggle /></div>
        <NotificationBell />
        {showProfileIcon && profileIcon("simple-profile-icon-desktop")}
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-50"
          role="dialog"
          aria-modal="true"
          data-testid="mobile-drawer"
        >
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={closeMobile}
            data-testid="mobile-drawer-overlay"
          />
          <div className="absolute inset-y-0 left-0 w-[80%] max-w-xs bg-[#0A0A0A] border-r border-[#262626] flex flex-col p-5 overflow-y-auto">
            <div className="flex items-center justify-between mb-8">
              {brand(closeMobile)}
              <button
                type="button"
                onClick={closeMobile}
                aria-label="Close menu"
                data-testid="mobile-menu-close"
                className="p-2 -mr-2 text-[#A3A3A3] hover:text-white"
              >
                <X strokeWidth={1.75} className="w-6 h-6" />
              </button>
            </div>
            {navList(closeMobile)}
            {userFooter}
          </div>
        </div>
      )}

      <main className="flex-1 min-w-0 overflow-x-hidden">
        {/* Iter374c · Fix 2 — Bypass the 1400px cap + lg:p-8 padding
             on the Festival Live Ops route so the courts grid can
             reach the true viewport width. Every other route keeps
             the standard reading-width container. */}
        <div className={isFestivalOps
          ? "w-full"
          : "px-4 pt-6 pb-24 sm:px-6 sm:pt-8 sm:pb-28 lg:p-8 max-w-[1400px] mx-auto"}>{user?.role === "student" && <AssessmentRiskBanner narrow={isSimple} />}{children}<ModeSwitchFooter /></div>
      </main>
    </div>
  );
}
